import { useTranslation } from "react-i18next";
import Steps from "./Steps";
import MustRead from "./MustRead";
import RevealingFromBottom from "../../../Common/RevealingFromBottom";
import { useScrollAnimate } from "../../../../hooks/useScrollAnimate";
import WarningIcon from "./WarningIcon";
import Title from "./Title";
import Buttons from "./Buttons";
import { useEffect, useMemo, useState } from "react";

export default function WarningsBox({
  stepsComplete,
  setStepsComplete,
  read,
  totalSteps,
}) {
  const { t } = useTranslation("global");
  const { ref } = useScrollAnimate();

  // Dynamic warnings array
  const warnings = useMemo(() => {
    const arr = t("warningsSection.content", { returnObjects: true }) || [];
    return Array.isArray(arr) ? arr : [];
  }, [t]);

  const numSteps = totalSteps; // same as warnings.length
  const lastIndex = Math.max(numSteps - 1, 0);

  const [currentText, setCurrentText] = useState(0);

  // If verified, jump user to the last slide and mark all steps complete
  useEffect(() => {
    const verified = localStorage.getItem("verified");
    if (verified) {
      setStepsComplete(numSteps);
      setCurrentText(lastIndex);
    } else {
      // keep indices in range on language/content changes
      setStepsComplete((pv) => Math.min(Math.max(pv, 0), numSteps));
      setCurrentText((idx) => Math.min(Math.max(idx, 0), lastIndex));
    }
  }, [numSteps, lastIndex, setStepsComplete]);

  // ✅ Keep stepsComplete in sync with the furthest slide reached
  // Steps are 1-based; slides are 0-based → reachedStep = currentText + 1
  useEffect(() => {
    setStepsComplete((prev) => Math.max(prev, currentText + 1));
  }, [currentText, setStepsComplete]);

  const renderLines = () => {
    if (numSteps === 0) return null;
    const text = warnings[currentText] || "";
    return text.split("|").map((line, index) => (
      <RevealingFromBottom key={index} duration={0.6} extraDelay={index / 20}>
        {line}
      </RevealingFromBottom>
    ));
  };

  return (
    <div ref={ref} id="warnings" className="mb-40 relative z-10">
      <Title />

      <div className="max-w-4xl flex flex-col mx-auto relative z-10">
        <Steps numSteps={numSteps} stepsComplete={stepsComplete} />

        <div className="p-2 lg:p-6 space-y-4 my-6 h-64 lg:h-48 font-medium border-gray-200 rounded-lg">
          {renderLines()}
        </div>

        <Buttons
          currentText={currentText}
          setCurrentText={setCurrentText}
          stepsComplete={stepsComplete}
          numSteps={numSteps}
        />
      </div>

      <WarningIcon />

      <img
        className="h-[400px] aspect-auto absolute -bottom-20 -right-20 opacity-10 blur-3xl"
        src={`${process.env.PUBLIC_URL}/warning/warningsShape.svg`}
        alt=""
      />

      <MustRead read={read} />
    </div>
  );
}
